import { useEffect, useState } from "react";
import { X, Smartphone, Monitor, ExternalLink, Eye } from "lucide-react";
import { mediaUrl } from "@/lib/api";

const VIEWS = [
  { k: "both", label: "Lado a lado" },
  { k: "mobile", label: "Mobile", icon: Smartphone },
  { k: "desktop", label: "Desktop", icon: Monitor },
];

function Frame({ src, width, height, label }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{label} · {width}px</span>
      <div className="border border-[#282520] bg-[#0C0B09] overflow-hidden shadow-[0_18px_60px_-20px_rgba(0,0,0,0.6)]" style={{ width: `${width}px`, height: `${height}px` }}>
        <iframe src={src} title={label} className="w-full h-full bg-[#0C0B09]" style={{ border: 0 }} />
      </div>
    </div>
  );
}

export function WatchPreviewModal({ slug, onClose }) {
  const [view, setView] = useState("both");
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const url = `/relogio/${slug}?preview=1`;
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex flex-col" data-testid="watch-preview-modal" onClick={onClose}>
      <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[#282520] bg-[#0C0B09]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <Eye className="w-4 h-4 text-[#C8AE6E]" />
          <span className="text-sm text-[#F3EFE6]">Pré-visualização da página pública</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 border border-[#282520]">
            {VIEWS.map(({ k, label, icon: Ic }) => (
              <button key={k} type="button" onClick={() => setView(k)} className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${view === k ? "bg-[#C8AE6E] text-[#0C0B09]" : "text-[#A39E93] hover:text-[#E6D5AC]"}`} data-testid={`watch-preview-view-${k}`}>
                {Ic && <Ic className="w-3.5 h-3.5" />} {label}
              </button>
            ))}
          </div>
          <a href={url} target="_blank" rel="noreferrer" className="btn-line !py-1.5 !px-3 text-xs" data-testid="watch-preview-open-tab"><ExternalLink className="w-3.5 h-3.5" /> Nova janela</a>
          <button type="button" onClick={onClose} className="p-1.5 text-[#A39E93] hover:text-[#E6D5AC]" aria-label="Fechar" data-testid="watch-preview-close"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-6 flex items-start justify-center gap-10" onClick={(e) => e.stopPropagation()}>
        {(view === "both" || view === "mobile") && <Frame src={url} width={390} height={820} label="Mobile" />}
        {(view === "both" || view === "desktop") && <Frame src={url} width={1280} height={820} label="Desktop" />}
      </div>
    </div>
  );
}

// Mini preview showing how a single photo renders in catalogue card and detail hero.
export function PhotoPreviewModal({ photo, brand, model, price, onClose }) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const src = mediaUrl(photo.lg || photo.md || photo.sm);
  const priceStr = price != null ? `${(price / 100).toLocaleString("pt-PT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €` : "Preço sob consulta";
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="photo-preview-modal" onClick={onClose}>
      <div className="bg-[#0C0B09] border border-[#282520] max-w-[1200px] w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#282520]">
          <div className="flex items-center gap-2"><Eye className="w-4 h-4 text-[#C8AE6E]" /><span className="text-sm text-[#F3EFE6]">Como esta foto fica no site</span></div>
          <button type="button" onClick={onClose} className="p-1.5 text-[#A39E93] hover:text-[#E6D5AC]" aria-label="Fechar" data-testid="photo-preview-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 grid md:grid-cols-[1fr_1.6fr] gap-10">
          <div className="space-y-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">Cartão do catálogo · mobile</div>
            <div style={{ width: 300 }} className="group">
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#161512]">
                <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{brand}</p>
                <p className="text-base text-[#F3EFE6] mt-1">{model}</p>
                <p className="text-sm text-[#C8AE6E] mt-2">{priceStr}</p>
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93] pt-4">Cartão do catálogo · desktop</div>
            <div style={{ width: 360 }}>
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#161512]">
                <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{brand}</p>
                <p className="text-base text-[#F3EFE6] mt-1">{model}</p>
                <p className="text-sm text-[#C8AE6E] mt-2">{priceStr}</p>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">Galeria da página do relógio</div>
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#161512]">
              <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
            </div>
            <p className="text-xs text-[#A39E93]">O site aplica recorte 4:5 central (<code className="text-[#E6D5AC]">object-cover</code>) para garantir uniformidade.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
